import { useEffect, useState, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  Grid,
  FormHelperText,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react'
import { ChevronDown } from 'lucide-react'
import {
  createUserSchema,
  updateUserSchema,
  type CreateUserFormData,
  type UpdateUserFormData,
} from '@/lib/validations/auth'
import { PasswordGenerator } from '@/components/PasswordGenerator'
import { apiClient } from '@/lib/api/client'
import { geographicService, type GeographicOption } from '@/lib/services/geographicService'
import { useToastNotification } from '@/lib/utils/useToastNotification'

interface Role {
  id: number
  name: string
}

interface Client {
  id: number
  name: string
}

interface Department {
  id: number
  name: string
}

interface AreaClusterOption {
  id: number
  name: string
  label?: string
}

interface SupervisorOption {
  id: number
  user_login: string
  user_email?: string
  first_name?: string
  last_name?: string
  employee_id?: string
  position?: string
}

interface Team {
  id: number
  name: string
}

interface ApplicationOption {
  id: number
  name: string
  code: string
  active?: boolean
}

function defaultApplicationAccess(apps: ApplicationOption[]): Record<string, { access: boolean; auditor: boolean }> {
  return apps.reduce((acc, app) => {
    acc[app.code] = { access: false, auditor: false }
    return acc
  }, {} as Record<string, { access: boolean; auditor: boolean }>)
}

interface UserFormProps {
  userId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function UserForm({ userId, onSuccess, onCancel }: UserFormProps) {
  const [roles, setRoles] = useState<Role[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [areaClusters, setAreaClusters] = useState<AreaClusterOption[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [applications, setApplications] = useState<ApplicationOption[]>([])
  const [supervisorOptions, setSupervisorOptions] = useState<SupervisorOption[]>([])
  const [isLoadingRoles, setIsLoadingRoles] = useState(true)
  const [isLoadingClients, setIsLoadingClients] = useState(false)
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false)
  const [isLoadingAreaClusters, setIsLoadingAreaClusters] = useState(false)
  const [isLoadingTeams, setIsLoadingTeams] = useState(false)
  const [isLoadingApplications, setIsLoadingApplications] = useState(false)
  const [isLoadingSupervisors, setIsLoadingSupervisors] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [userStatus, setUserStatus] = useState<number>(1)
  const [vicidialAgentPassSet, setVicidialAgentPassSet] = useState(false)
  const [vicidialPhonePassSet, setVicidialPhonePassSet] = useState(false)
  const [reportsToSearch, setReportsToSearch] = useState('')
  const [reportsToDropdownOpen, setReportsToDropdownOpen] = useState(false)
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false)
  const hasSetAppAccessDefault = useRef(false)
  const { showError, showSuccess } = useToastNotification()
  const isEditMode = !!userId

  const defaultAppAccess = useMemo(
    () => defaultApplicationAccess(applications),
    [applications]
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CreateUserFormData | UpdateUserFormData>({
    resolver: zodResolver(isEditMode ? updateUserSchema : createUserSchema),
    defaultValues: {
      user_status: 1,
      area_cluster: '',
      reports_to: '',
      team_ids: [],
      application_access: {},
    },
  })

  const passwordValue = watch('user_pass')
  const selectedRoleId = watch('user_role')
  const selectedRole = roles.find((r) => r.id.toString() === selectedRoleId)
  const isClientRole = selectedRole?.name?.toLowerCase() === 'client'
  const reportsToValue = watch('reports_to')
  const teamIds = watch('team_ids') ?? []
  const applicationAccess = watch('application_access') ?? {}
  const selectedSupervisor = supervisorOptions.find((u) => u.id.toString() === reportsToValue)

  const getSupervisorLabel = (u: SupervisorOption) =>
    [u.employee_id, u.first_name, u.last_name, u.user_login].filter(Boolean).join(' – ')
  const supervisorDisplayLabel = selectedSupervisor
    ? getSupervisorLabel(selectedSupervisor)
    : reportsToValue
      ? `User #${reportsToValue}`
      : ''

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await apiClient.get('/options/roles')
        const rolesData = Array.isArray(response.data) ? response.data : response.data.data || []
        const filteredRoles = rolesData.filter((role: Role) => role.name !== 'Developer Account')
        setRoles(filteredRoles)
      } catch (error: any) {
        showError(error)
      } finally {
        setIsLoadingRoles(false)
      }
    }
    fetchRoles()
  }, [showError])

  // Fetch departments on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      setIsLoadingDepartments(true)
      try {
        const response = await apiClient.get('/options/departments')
        const departmentsData = response.data?.data || response.data || []
        setDepartments(Array.isArray(departmentsData) ? departmentsData : [])
      } catch (error: any) {
        showError(error)
      } finally {
        setIsLoadingDepartments(false)
      }
    }
    fetchDepartments()
  }, [showError])

  // Fetch area clusters for user assignment (user_meta.area_cluster_id)
  useEffect(() => {
    const fetchAreaClusters = async () => {
      setIsLoadingAreaClusters(true)
      try {
        const options = await geographicService.getAreaClusterOptions()
        setAreaClusters(
          options.map((option: GeographicOption) => ({
            id: option.id,
            name: option.name ?? option.label,
            label: option.label,
          }))
        )
      } catch (error: any) {
        showError(error)
      } finally {
        setIsLoadingAreaClusters(false)
      }
    }
    fetchAreaClusters()
  }, [showError])

  // Fetch all clients for dropdown (options endpoint returns full list, no pagination)
  useEffect(() => {
    const fetchClients = async () => {
      setIsLoadingClients(true)
      try {
        const response = await apiClient.get('/options/clients')
        const clientsData = Array.isArray(response.data) ? response.data : response.data?.data || []
        setClients(clientsData)
      } catch (error: any) {
        showError(error)
      } finally {
        setIsLoadingClients(false)
      }
    }
    fetchClients()
  }, [showError])

  // Fetch teams for Team Assignment (multi-select dropdown from database)
  useEffect(() => {
    const fetchTeams = async () => {
      setIsLoadingTeams(true)
      try {
        const response = await apiClient.get('/options/teams')
        const teamsData = response.data?.data ?? response.data ?? []
        setTeams(Array.isArray(teamsData) ? teamsData : [])
      } catch {
        setTeams([])
      } finally {
        setIsLoadingTeams(false)
      }
    }
    fetchTeams()
  }, [])

  // Fetch applications for Web App Access & Auditor (from database)
  useEffect(() => {
    const fetchApplications = async () => {
      setIsLoadingApplications(true)
      try {
        const response = await apiClient.get('/system-settings/applications', { params: { per_page: 100 } })
        const list = response.data?.data ?? response.data ?? []
        const items = Array.isArray(list) ? list : (list as any).data ?? []
        setApplications(items.filter((a: ApplicationOption) => a.code && a.active !== false))
      } catch {
        setApplications([])
      } finally {
        setIsLoadingApplications(false)
      }
    }
    fetchApplications()
  }, [])

  // In create mode, set application_access default once when applications load
  useEffect(() => {
    if (!isEditMode && applications.length > 0 && !hasSetAppAccessDefault.current) {
      setValue('application_access', defaultApplicationAccess(applications))
      hasSetAppAccessDefault.current = true
    }
  }, [applications, isEditMode, setValue])

  // Search users for "Reports To" (supervisor) when user types 3+ chars
  useEffect(() => {
    if (reportsToSearch.trim().length < 3) {
      setSupervisorOptions([])
      return
    }
    const timer = setTimeout(() => {
      const runSearch = async () => {
        setIsLoadingSupervisors(true)
        try {
          const response = await apiClient.get(`/options/users/search?search=${encodeURIComponent(reportsToSearch.trim())}`)
          const data = Array.isArray(response.data) ? response.data : (response.data as any)?.data ?? []
          setSupervisorOptions(data)
          setReportsToDropdownOpen(true)
        } catch {
          setSupervisorOptions([])
        } finally {
          setIsLoadingSupervisors(false)
        }
      }
      runSearch()
    }, 300)
    return () => clearTimeout(timer)
  }, [reportsToSearch])

  useEffect(() => {
    if (isEditMode && userId) {
      const fetchUser = async () => {
        try {
          const response = await apiClient.get(`/user-management/users/${userId}`)
          const user = response.data?.data || response.data || {}

          let userRole = ''
          if (user.user_role) {
            if (typeof user.user_role === 'string') {
              try {
                const parsed = JSON.parse(user.user_role)
                userRole = parsed.id ? parsed.id.toString() : ''
              } catch {
                if (roles.length > 0) {
                  const role = roles.find(
                    (r) => r.name === user.user_role || r.id.toString() === user.user_role
                  )
                  userRole = role ? role.id.toString() : ''
                } else {
                  userRole = user.role_id ? user.role_id.toString() : ''
                }
              }
            } else if (user.user_role?.id) {
              userRole = user.user_role.id.toString()
            }
          } else if (user.role_id) {
            userRole = user.role_id.toString()
          }

          let statusValue = 1
          if (user.user_status !== undefined && user.user_status !== null) {
            if (typeof user.user_status === 'string') {
              if (user.user_status === 'Inactive' || user.user_status === '0') {
                statusValue = 0
              } else if (user.user_status === 'Suspended' || user.user_status === '2') {
                statusValue = 2
              } else if (user.user_status === 'Active' || user.user_status === '1') {
                statusValue = 1
              }
            } else {
              statusValue = Number(user.user_status)
            }
          }

          const first_name = user.first_name ?? user.user_details?.first_name ?? ''
          const last_name = user.last_name ?? user.user_details?.last_name ?? ''
          const employee_id = user.employee_id ?? user.user_details?.employee_id ?? ''
          const position = user.position ?? user.user_details?.position ?? ''
          const vicidial_agent_user = user.vicidial_agent_user ?? ''
          const vicidial_phone_login = user.vicidial_phone_login ?? ''
          setVicidialAgentPassSet(Boolean(user.vicidial_agent_pass_set))
          setVicidialPhonePassSet(Boolean(user.vicidial_phone_pass_set))

          // Parse department and client if they exist
          let departmentId = ''
          let clientId = ''
          
          if (user.department) {
            if (typeof user.department === 'string') {
              try {
                const parsed = JSON.parse(user.department)
                departmentId = parsed.id ? parsed.id.toString() : ''
              } catch {
                departmentId = user.department
              }
            } else if (user.department?.id) {
              departmentId = user.department.id.toString()
            }
          }

          if (user.client_id) {
            clientId = user.client_id.toString()
          }

          let areaClusterId = ''
          if (user.area_cluster_id != null) {
            areaClusterId = user.area_cluster_id.toString()
          } else if (user.cluster_id != null) {
            areaClusterId = user.cluster_id.toString()
          }

          // Reports to / supervisor (user_meta.reports_to)
          let reportsToId = ''
          if (user.reports_to != null) {
            reportsToId = user.reports_to.toString()
          } else if (user.reports_to_user?.id != null) {
            reportsToId = user.reports_to_user.id.toString()
          }

          // Team Assignment
          let loadedTeamIds: number[] = []
          if (Array.isArray(user.teams)) {
            loadedTeamIds = user.teams.map((t: { id?: number }) => Number(t.id)).filter((id: number) => !Number.isNaN(id))
          } else if (Array.isArray(user.team_ids)) {
            loadedTeamIds = user.team_ids.map((id: number) => Number(id)).filter((id: number) => !Number.isNaN(id))
          }

          // Web App Access & Auditor (merge with current applications from DB)
          const appDefault = defaultApplicationAccess(applications)
          const loadedAppAccess = user.application_access && typeof user.application_access === 'object'
            ? { ...appDefault, ...user.application_access }
            : { ...appDefault }

          reset({
            user_login: user.user_login || '',
            user_email: user.user_email || '',
            user_pass: '',
            employee_id,
            first_name,
            last_name,
            position,
            vicidial_agent_user,
            vicidial_phone_login,
            vicidial_agent_pass: '',
            vicidial_phone_pass: '',
            user_role: userRole || '',
            client_id: clientId,
            department: departmentId,
            area_cluster: areaClusterId,
            reports_to: reportsToId,
            team_ids: loadedTeamIds,
            application_access: loadedAppAccess,
            user_status: statusValue,
          })

          setValue('first_name', first_name)
          setValue('last_name', last_name)
          setValue('employee_id', employee_id)
          setValue('position', position)
          setValue('vicidial_agent_user', vicidial_agent_user)
          setValue('vicidial_phone_login', vicidial_phone_login)
          setValue('vicidial_agent_pass', '')
          setValue('vicidial_phone_pass', '')
          setValue('user_role', userRole || '')
          setValue('client_id', clientId)
          setValue('department', departmentId)
          setValue('area_cluster', areaClusterId)
          setValue('reports_to', reportsToId)
          setValue('team_ids', loadedTeamIds)
          setValue('application_access', loadedAppAccess)
          setValue('user_status', statusValue)
          setUserStatus(statusValue)

          if (reportsToId && (user.reports_to_user || user.reports_to_user_id)) {
            const supervisor = user.reports_to_user
            if (supervisor && typeof supervisor === 'object') {
              setSupervisorOptions([{
                id: supervisor.id,
                user_login: supervisor.user_login ?? '',
                user_email: supervisor.user_email,
                first_name: supervisor.first_name,
                last_name: supervisor.last_name,
                employee_id: supervisor.employee_id,
                position: supervisor.position,
              }])
            }
          }
        } catch (error: any) {
          showError(error)
        }
      }
      fetchUser()
    }
  }, [isEditMode, userId, reset, showError, setValue, roles, applications])

  const onSubmit = async (data: CreateUserFormData | UpdateUserFormData) => {
    setIsSubmitting(true)
    try {
      const selectedRole = roles.find((r) => r.id.toString() === data.user_role)
      if (!selectedRole) {
        showError({ message: 'Please select a valid role' })
        setIsSubmitting(false)
        return
      }

      // Backend expects user_role as a JSON object with an 'id' property
      // The 'id' must be a number (not a string) and cannot be 1 (Developer Account)
      const roleId = Number(selectedRole.id)
      if (isNaN(roleId) || roleId === 1) {
        showError({ message: 'Invalid role selected' })
        setIsSubmitting(false)
        return
      }
      
      const roleObject = { id: roleId }
      
      // Validate the role object structure
      if (!roleObject || typeof roleObject.id !== 'number' || roleObject.id === 1) {
        showError({ message: 'Invalid role data structure' })
        setIsSubmitting(false)
        return
      }

      // Validate Client role requirements
      const isClient = selectedRole.name?.toLowerCase() === 'client'
      if (isClient) {
        if (!data.client_id) {
          showError({ message: 'Client is required when role is Client' })
          setIsSubmitting(false)
          return
        }
      }

      const payload: Record<string, any> = {
        user_login: data.user_login,
        user_email: data.user_email,
        user_role: roleObject, // Send as object, backend will convert to JSON string
        employee_id: data.employee_id,
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        position: data.position || '',
        vicidial_agent_user: data.vicidial_agent_user || '',
        vicidial_phone_login: data.vicidial_phone_login || '',
      }

      // Vicidial passwords: only send when admin typed a new value (never pre-filled).
      if (data.vicidial_agent_pass) {
        payload.vicidial_agent_pass = data.vicidial_agent_pass
      }
      if (data.vicidial_phone_pass) {
        payload.vicidial_phone_pass = data.vicidial_phone_pass
      }

      // Add department if provided (as object, backend will convert to JSON string)
      if (data.department) {
        const departmentId = Number(data.department)
        if (!isNaN(departmentId)) {
          payload.department = { id: departmentId }
        }
      }

      if (data.area_cluster) {
        const areaClusterId = Number(data.area_cluster)
        if (!isNaN(areaClusterId)) {
          payload.area_cluster_id = areaClusterId
        }
      }

      // Reports to / supervisor (user_meta.reports_to) – organizational hierarchy (optional)
      if (data.reports_to) {
        const reportsToId = Number(data.reports_to)
        if (!isNaN(reportsToId)) {
          payload.reports_to = reportsToId
        }
      } else if (isEditMode) {
        payload.reports_to = null
      }

      // Add client if Client role is selected
      if (isClientRole) {
        if (data.client_id) {
          payload.client_id = Number(data.client_id)
        }
      }

      // Team Assignment (team_members)
      if (data.team_ids && data.team_ids.length > 0) {
        payload.team_ids = data.team_ids
      }

      // Web App Access & Auditor
      if (data.application_access && Object.keys(data.application_access).length > 0) {
        payload.application_access = data.application_access
      }

      if (isEditMode) {
        const updateData = data as UpdateUserFormData
        if (updateData.user_pass && updateData.user_pass.trim() !== '') {
          payload.user_pass = updateData.user_pass
        }
        payload.user_status = userStatus
      } else {
        const createData = data as CreateUserFormData
        payload.user_pass = createData.user_pass
        payload.user_status = userStatus
      }

      if (isEditMode) {
        await apiClient.put(`/user-management/users/${userId}`, payload)
        showSuccess('Success', 'User updated successfully')
      } else {
        await apiClient.post('/user-management/users', payload)
        showSuccess('Success', 'User created successfully')
      }

      onSuccess?.()
    } catch (error: any) {
      // Handle validation errors
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const validationErrors = error.response.data.errors
        const errorMessages: string[] = []
        
        Object.keys(validationErrors).forEach((field) => {
          if (Array.isArray(validationErrors[field])) {
            errorMessages.push(...validationErrors[field])
          } else {
            errorMessages.push(validationErrors[field])
          }
        })
        
        showError({ message: errorMessages.join(', ') })
      } else {
        showError(error)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePasswordGenerated = (password: string) => {
    setValue('user_pass', password)
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Username / Client ID <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.user_login}>
            <Input {...register('user_login')} placeholder="e.g. johndoe" />
            <FormErrorMessage>{errors.user_login?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Email <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.user_email}>
            <Input {...register('user_email')} type="email" placeholder="e.g. john.doe@example.com" />
            <FormErrorMessage>{errors.user_email?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Employee ID
          </FormLabel>
          <FormControl isInvalid={!!errors.employee_id}>
            <Input {...register('employee_id')} placeholder="e.g. ALS-25-520" />
            <FormErrorMessage>{errors.employee_id?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            First Name <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.first_name}>
            <Input {...register('first_name')} placeholder="e.g. John" />
            <FormErrorMessage>{errors.first_name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Last Name <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.last_name}>
            <Input {...register('last_name')} placeholder="e.g. Doe" />
            <FormErrorMessage>{errors.last_name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Position
          </FormLabel>
          <FormControl isInvalid={!!errors.position}>
            <Input {...register('position')} placeholder="e.g. Frontend Developer" />
            <FormErrorMessage>{errors.position?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Vicidial Agent Username
          </FormLabel>
          <FormControl isInvalid={!!errors.vicidial_agent_user}>
            <Input
              {...register('vicidial_agent_user')}
              placeholder="e.g. Agent1001 — leave blank if this user doesn't use Vicidial"
              autoComplete="off"
            />
            <FormErrorMessage>{errors.vicidial_agent_user?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Vicidial Agent Password
          </FormLabel>
          <FormControl isInvalid={!!errors.vicidial_agent_pass}>
            <Input
              type="password"
              {...register('vicidial_agent_pass')}
              placeholder={
                isEditMode && vicidialAgentPassSet
                  ? 'Leave blank to keep current password'
                  : 'Vicidial agent password (VD_pass)'
              }
              autoComplete="new-password"
            />
            <FormErrorMessage>{errors.vicidial_agent_pass?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Vicidial Phone Login
          </FormLabel>
          <FormControl isInvalid={!!errors.vicidial_phone_login}>
            <Input
              {...register('vicidial_phone_login')}
              placeholder="Usually same as agent username (e.g. Agent1001)"
              autoComplete="off"
            />
            <FormErrorMessage>{errors.vicidial_phone_login?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Vicidial Phone Password
          </FormLabel>
          <FormControl isInvalid={!!errors.vicidial_phone_pass}>
            <Input
              type="password"
              {...register('vicidial_phone_pass')}
              placeholder={
                isEditMode && vicidialPhonePassSet
                  ? 'Leave blank to keep current password'
                  : 'Vicidial phone password'
              }
              autoComplete="new-password"
            />
            <FormErrorMessage>{errors.vicidial_phone_pass?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Department
          </FormLabel>
          <FormControl isInvalid={!!errors.department}>
            <Select
              {...register('department')}
              placeholder={isLoadingDepartments ? 'Loading departments...' : 'Select Department'}
              isDisabled={isLoadingDepartments}
            >
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id.toString()}>
                  {dept.name}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.department?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Area Cluster
          </FormLabel>
          <FormControl isInvalid={!!errors.area_cluster}>
            <Select
              {...register('area_cluster')}
              placeholder={isLoadingAreaClusters ? 'Loading area clusters...' : 'Select area cluster'}
              isDisabled={isLoadingAreaClusters}
            >
              {areaClusters.map((areaCluster) => (
                <option key={areaCluster.id} value={areaCluster.id.toString()}>
                  {areaCluster.label ?? areaCluster.name}
                </option>
              ))}
            </Select>
            <FormHelperText>Assign the user to a geographic area cluster.</FormHelperText>
            <FormErrorMessage>{errors.area_cluster?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Role <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.user_role}>
            <Select
              {...register('user_role')}
              placeholder={isLoadingRoles ? 'Loading roles...' : 'Select Role'}
              isDisabled={isLoadingRoles}
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id.toString()}>
                  {role.name}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.user_role?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        {isClientRole && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Client <Text as="span" color="red.500">*</Text>
            </FormLabel>
            <FormControl isInvalid={!!errors.client_id}>
              <Select
                {...register('client_id')}
                placeholder={isLoadingClients ? 'Loading clients...' : 'Select Client'}
                isDisabled={isLoadingClients}
              >
                {clients.map((client) => (
                  <option key={client.id} value={client.id.toString()}>
                    {client.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.client_id?.message}</FormErrorMessage>
            </FormControl>
          </Grid>
        )}

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Reports To (Supervisor)
          </FormLabel>
          <FormControl isInvalid={!!errors.reports_to}>
            <Box position="relative">
              {reportsToValue ? (
                <Box
                  px={3}
                  py={2}
                  borderWidth={1}
                  borderColor="gray.200"
                  borderRadius="md"
                  bg="gray.50"
                  _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Text fontSize="sm" isTruncated>
                    {supervisorDisplayLabel}
                  </Text>
                  <Button
                    type="button"
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    flexShrink={0}
                    onClick={() => {
                      setValue('reports_to', '')
                      setReportsToSearch('')
                      setSupervisorOptions([])
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              ) : (
                <>
                  <Input
                    placeholder="Type 3+ characters to search users"
                    value={reportsToSearch}
                    onChange={(e) => setReportsToSearch(e.target.value)}
                    onFocus={() => setReportsToDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setReportsToDropdownOpen(false), 200)}
                  />
                  {reportsToDropdownOpen && supervisorOptions.length > 0 && (
                    <Box
                      position="absolute"
                      zIndex={2}
                      top="100%"
                      left={0}
                      right={0}
                      mt={1}
                      bg="white"
                      borderWidth={1}
                      borderColor="gray.200"
                      borderRadius="md"
                      shadow="md"
                      maxH="200px"
                      overflowY="auto"
                      _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
                    >
                      {supervisorOptions.map((u) => (
                        <Box
                          key={u.id}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: 'gray.100' }}
                          _dark={{ _hover: { bg: 'gray.700' } }}
                          onClick={() => {
                            setValue('reports_to', u.id.toString())
                            setSupervisorOptions([u])
                            setReportsToDropdownOpen(false)
                          }}
                        >
                          {getSupervisorLabel(u)}
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>
            <FormHelperText>Immediate supervisor for organizational hierarchy.</FormHelperText>
            <FormErrorMessage>{errors.reports_to?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Team Assignment
          </FormLabel>
          <FormControl>
            {isLoadingTeams ? (
              <Text fontSize="sm" color="gray.500">Loading teams...</Text>
            ) : teams.length === 0 ? (
              <FormHelperText>No teams available. Teams can be configured in Organization → Team.</FormHelperText>
            ) : (
              <Menu isOpen={teamDropdownOpen} onOpen={() => setTeamDropdownOpen(true)} onClose={() => setTeamDropdownOpen(false)} closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDown size={16} />}
                  variant="outline"
                  w="full"
                  textAlign="left"
                  fontWeight="normal"
                >
                  {teamIds.length === 0
                    ? 'Select teams...'
                    : `${teamIds.length} team(s) selected: ${teams.filter((t) => teamIds.includes(t.id)).map((t) => t.name).join(', ')}`}
                </MenuButton>
                <MenuList minW="280px" maxH="300px" overflowY="auto">
                  {teams.map((team) => {
                    const isChecked = teamIds.includes(team.id)
                    return (
                      <MenuItem
                        key={team.id}
                        closeOnSelect={false}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          const next = isChecked
                            ? teamIds.filter((id) => id !== team.id)
                            : [...teamIds, team.id]
                          setValue('team_ids', next, { shouldValidate: true })
                        }}
                      >
                        <Checkbox isChecked={isChecked} pointerEvents="none" w="full">
                          {team.name}
                        </Checkbox>
                      </MenuItem>
                    )
                  })}
                </MenuList>
              </Menu>
            )}
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Web App Access &amp; Auditor
          </FormLabel>
          <FormControl>
            <Text fontSize="sm" mb={3} color="gray.600" _dark={{ color: 'gray.400' }}>
              Select which web apps this user can access and auditor privileges (from database):
            </Text>
            {isLoadingApplications ? (
              <Text fontSize="sm" color="gray.500">Loading applications...</Text>
            ) : applications.length === 0 ? (
              <FormHelperText>No applications configured. Add applications in System Settings → Application.</FormHelperText>
            ) : (
              <VStack align="stretch" spacing={4} pl={2}>
                {applications.map((app) => {
                  const acc = applicationAccess[app.code] ?? { access: false, auditor: false }
                  return (
                    <Box key={app.code} pl={2} borderLeftWidth={2} borderColor="gray.200" _dark={{ borderColor: 'gray.600' }}>
                      <Checkbox
                        isChecked={acc.access}
                        onChange={(e) => {
                          const access = e.target.checked
                          const next = { ...applicationAccess, [app.code]: { access, auditor: access ? acc.auditor : false } }
                          setValue('application_access', next, { shouldValidate: true })
                        }}
                      >
                        {app.name}
                      </Checkbox>
                      <Box pl={6} mt={1}>
                        <Checkbox
                          isChecked={acc.auditor}
                          isDisabled={!acc.access}
                          onChange={(e) => {
                            const next = { ...applicationAccess, [app.code]: { ...acc, auditor: e.target.checked } }
                            setValue('application_access', next, { shouldValidate: true })
                          }}
                        >
                          Auditor – View &amp; Edit
                        </Checkbox>
                      </Box>
                    </Box>
                  )
                })}
              </VStack>
            )}
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Password {!isEditMode && (
              <Text as="span" color="red.500">
                *
              </Text>
            )}
          </FormLabel>
          <FormControl isInvalid={!!errors.user_pass} isRequired={!isEditMode}>
            <PasswordGenerator value={passwordValue || ''} onChange={handlePasswordGenerated} label="" />
            {!passwordValue && isEditMode && (
              <FormHelperText>Leave blank to keep the current password.</FormHelperText>
            )}
            <FormErrorMessage>{errors.user_pass?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Status
          </FormLabel>
          <FormControl>
            <Select
              value={userStatus}
              onChange={(e) => {
                const value = Number(e.target.value)
                setUserStatus(value)
                setValue('user_status', value, { shouldValidate: true })
              }}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
              <option value={2}>Suspended</option>
            </Select>
            <FormHelperText>Control whether this user can log in.</FormHelperText>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Saving...' : 'Creating...'}>
            {isEditMode ? 'Save' : 'Create User'}
            {isSubmitting && (
              <Text as="span" ml={2} fontSize="sm">
                ...
              </Text>
            )}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}


